import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import { IUserInfo } from "../../store/actions";

interface DeleteReworkButtonProps {
  chassisNo: string;
  stationName: string;
  onClearComplete?: () => void;
}

const DeleteReworkButton: React.FC<DeleteReworkButtonProps> = ({
  chassisNo,
  stationName,
  onClearComplete,
}) => {
  const axiosPrivate = useAxiosPrivate();
  const [open, setOpen] = useState(false);

  const authInfo: IUserInfo = useSelector(
    (state: any) => state.userAuthInfoState
  );
  const userRole = authInfo.roles;

  const hasRequiredRole = Array.isArray(userRole)
    ? userRole.includes("qa-manager-qc")
    : userRole === "qa-manager-qc";

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDelete = async () => {
    if (!chassisNo || !stationName) {
      toast.error("Chassis number and station name are required.");
      handleClose();
      return;
    }

    try {
      const apiEndpoint = `/clear_rework_data/${chassisNo}?station_name=${encodeURIComponent(
        stationName
      )}`;

      await toast.promise(
        axiosPrivate.post(apiEndpoint, { chassis_no: chassisNo }),
        {
          pending: "Clearing rework data...",
          success: "Rework data cleared successfully!",
          error: "Failed to clear rework data. Item may not exist.",
        }
      );

      if (onClearComplete) {
        onClearComplete();
      }
    } catch (error) {
      console.error("Error clearing rework data:", error);
      toast.error("An error occurred while clearing rework data.");
    }

    handleClose();
  };

  if (!hasRequiredRole) {
    return null;
  }

  return (
    <>
      <Button
        variant="contained"
        color="error"
        onClick={handleClickOpen}
        disabled={!chassisNo || !stationName}
      >
        Clear Data
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete rework data for chassis DC
            {chassisNo}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeleteReworkButton;
